import React, { memo, useCallback, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import RichTextEditor, { descToHtml } from './RichTextEditor';
import ClientsCombobox from './ClientsCombobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { DialogFooter } from './ui/dialog';
import {
  Loader2,
  Upload,
  FileText,
  Brain,
  Sparkles,
} from 'lucide-react';

const JobFormContent = memo(({ 
  formData, 
  setFormData,
  clients, 
  users, 
  formLoading,
  pdfParsing,
  aiGenerating,
  onPdfUpload,
  onAIGenerate,
  onSubmit,
  submitText 
}) => {
  const scrollContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Save scroll position before any update
  const saveScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }
  }, []);

  // Restore scroll position after render
  useEffect(() => {
    if (scrollContainerRef.current && scrollPositionRef.current > 0) {
      scrollContainerRef.current.scrollTop = scrollPositionRef.current;
    }
  });

  // Handle field change with scroll preservation
  const handleChange = useCallback((field, value) => {
    saveScroll();
    setFormData(prev => ({ ...prev, [field]: value }));
  }, [setFormData, saveScroll]);

  // Handle input change event
  const handleInputChange = useCallback((field) => (e) => {
    handleChange(field, e.target.value);
  }, [handleChange]);

  // Handle number input change
  const handleNumberChange = useCallback((field) => (e) => {
    handleChange(field, parseInt(e.target.value) || 0);
  }, [handleChange]);

  return (
    <>
      <div 
        ref={scrollContainerRef}
        className="max-h-[65vh] overflow-y-auto pr-4"
        style={{ scrollBehavior: 'auto' }}
      >
        <div className="space-y-4 pb-4">
          {/* PDF Upload Section */}
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-teal-600" />
                <div>
                  <p className="font-medium text-slate-700">Upload Job Description PDF</p>
                  <p className="text-sm text-slate-500">AI will extract job details automatically</p>
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={onPdfUpload}
                  className="hidden"
                  id="job-pdf-upload-form"
                  disabled={pdfParsing}
                />
                <label htmlFor="job-pdf-upload-form">
                  <Button
                    type="button"
                    variant="outline"
                    className="cursor-pointer"
                    disabled={pdfParsing}
                    asChild
                  >
                    <span>
                      {pdfParsing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Parsing...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload PDF
                        </>
                      )}
                    </span>
                  </Button>
                </label>
              </div>
            </div>
          </div>

          {/* AI JD Generator Section */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Brain className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="font-medium text-purple-700">AI Job Description Generator</p>
                  <p className="text-sm text-purple-500">Enter a title and let AI create the full JD</p>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
                onClick={onAIGenerate}
                disabled={aiGenerating || !formData.title}
              >
                {aiGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate with AI
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-500">Or fill manually</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              <ClientsCombobox
                clients={clients}
                value={formData.client_id}
                onChange={(v) => handleChange('client_id', v)}
                testid="job-form-client"
              />
            </div>
            <div className="space-y-2">
              <Label>Job Title *</Label>
              <Input
                value={formData.title}
                onChange={handleInputChange('title')}
                placeholder="e.g. Senior Software Engineer"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <RichTextEditor
              value={descToHtml(formData.description)}
              onChange={(html) => {
                // Synthesize a synthetic event so handleInputChange('description')
                // (which expects e.target.value) keeps working unchanged.
                handleInputChange('description')({ target: { value: html } });
              }}
              placeholder="Job description, responsibilities, key skills..."
              minHeight={280}
            />
          </div>

          <div className="space-y-2">
            <Label>Required Skills (comma-separated)</Label>
            <Input
              value={formData.skills}
              onChange={handleInputChange('skills')}
              placeholder="e.g. React, Node.js, MongoDB"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Min Experience (years)</Label>
              <Input
                type="number"
                min="0"
                value={formData.experience_min}
                onChange={handleNumberChange('experience_min')}
              />
            </div>
            <div className="space-y-2">
              <Label>Max Experience (years)</Label>
              <Input
                type="number"
                min="0"
                value={formData.experience_max}
                onChange={handleNumberChange('experience_max')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Location</Label>
              <Input
                value={formData.location}
                onChange={handleInputChange('location')}
                placeholder="e.g. Bangalore, Remote"
              />
            </div>
            <div className="space-y-2">
              <Label>Job Type</Label>
              <Select
                value={formData.job_type}
                onValueChange={(v) => handleChange('job_type', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_time">Full Time</SelectItem>
                  <SelectItem value="part_time">Part Time</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="internship">Internship</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Min Salary (INR LPA)</Label>
              <Input
                type="number"
                min="0"
                value={formData.salary_min}
                onChange={handleInputChange('salary_min')}
                placeholder="e.g. 10"
              />
            </div>
            <div className="space-y-2">
              <Label>Max Salary (INR LPA)</Label>
              <Input
                type="number"
                min="0"
                value={formData.salary_max}
                onChange={handleInputChange('salary_max')}
                placeholder="e.g. 20"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SLA Days</Label>
              <Input
                type="number"
                min="1"
                value={formData.sla_days}
                onChange={handleInputChange('sla_days')}
                placeholder="Days to fill position"
              />
            </div>
            <div className="space-y-2">
              <Label>Hiring Manager</Label>
              <Input
                value={formData.hiring_manager}
                onChange={handleInputChange('hiring_manager')}
                placeholder="Hiring manager name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                Vacancies
                <span className="text-xs text-slate-400 font-normal">
                  (auto-closes when filled)
                </span>
              </Label>
              <Input
                type="number"
                min="1"
                max="999"
                value={formData.headcount ?? 1}
                onChange={handleInputChange('headcount')}
                placeholder="1"
                data-testid="job-headcount-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Assign Recruiters</Label>
            <Select
              value={formData.assigned_recruiters?.[0] || ''}
              onValueChange={(v) => handleChange('assigned_recruiters', v ? [v] : [])}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select recruiter" />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {users
                  .filter(
                    (u) =>
                      u.role === 'recruiter' ||
                      u.role === 'team_lead' ||
                      u.role === 'delivery_manager',
                  )
                  .map((recruiter) => (
                    <SelectItem key={recruiter.id} value={recruiter.id}>
                      {recruiter.name}
                      {recruiter.role !== 'recruiter' ? ` · ${recruiter.role.replace('_', ' ')}` : ''}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={formData.status}
              onValueChange={(v) => handleChange('status', v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <DialogFooter className="mt-4">
        <Button
          onClick={onSubmit}
          disabled={formLoading || !formData.client_id || !formData.title}
          className="bg-teal-700 hover:bg-teal-800"
        >
          {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {submitText}
        </Button>
      </DialogFooter>
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific props change
  return (
    prevProps.formData === nextProps.formData &&
    prevProps.formLoading === nextProps.formLoading &&
    prevProps.pdfParsing === nextProps.pdfParsing &&
    prevProps.aiGenerating === nextProps.aiGenerating &&
    prevProps.clients === nextProps.clients &&
    prevProps.users === nextProps.users
  );
});

JobFormContent.displayName = 'JobFormContent';

export default JobFormContent;
